import React, { useState, useEffect, useMemo } from "react";
import ExcelJS from "exceljs";
import { apiFetch } from "./api";
import {
  ymd,
  currentMonthRange,
  previousMonthRange,
  firstHalfRange,
  lastHalfRange,
} from "./dateRanges";
import {
  DEFAULT_TEMPLATES,
  fmtAmount,
  buildEmailText,
} from "./emailTemplate";

/* ---------- helpers ---------- */
function toISODate(ts) {
  const d = new Date(ts);
  return ymd(d);
}

// "2026-07-07" -> "07.07.2026" (Estonian date format)
function fmtDateEt(iso) {
  if (!iso) return "…";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

// Human-readable period for the subject and the {periood} token.
// With no dates the report covers everything, so say so instead of "… – …".
function periodLabel(start, end) {
  if (start && end) return `${fmtDateEt(start)} – ${fmtDateEt(end)}`;
  if (start) return `alates ${fmtDateEt(start)}`;
  if (end) return `kuni ${fmtDateEt(end)}`;
  return "kogu periood";
}

// "2026-06-15T09:03:00+00:00" -> "15.06.2026 09:03" (straight from the ISO string, no tz math)
function fmtDateTimeEt(ts) {
  if (!ts) return "";
  const [datePart, timePartRaw] = String(ts).split("T");
  const [y, m, d] = (datePart || "").split("-");
  const time = (timePartRaw || "").slice(0, 5);
  if (!y || !m || !d) return String(ts);
  return `${d}.${m}.${y}${time ? " " + time : ""}`;
}

function sanitizeFilePart(s) {
  return String(s || "").replace(/[^\p{L}\p{N}_-]+/gu, "_").replace(/^_+|_+$/g, "");
}

/* ---------- summary builders ---------- */
function buildSummary(rows) {
  const byAssortment = new Map();
  for (const r of rows) {
    const sortiment = r.sortiment || "—";
    const amount = Number(r.amount) || 0;
    if (!byAssortment.has(sortiment)) {
      byAssortment.set(sortiment, { sortiment, total: 0, routes: new Map() });
    }
    const a = byAssortment.get(sortiment);
    a.total += amount;

    const from = r.from || "—";
    const to = r.to || "—";
    const key = `${from}→${to}`;
    if (!a.routes.has(key)) a.routes.set(key, { from, to, amount: 0, count: 0 });
    const route = a.routes.get(key);
    route.amount += amount;
    route.count += 1;
  }
  return Array.from(byAssortment.values())
    .map((a) => ({
      ...a,
      routes: Array.from(a.routes.values()).sort((x, y) => y.amount - x.amount),
    }))
    .sort((x, y) => y.total - x.total);
}

// Three levels: sortiment -> route (Lähtekoht → Sihtkoht) -> day,
// with summed totals + counts carried on the two upper levels.
function buildDailyHierarchy(rows) {
  const byAssortment = new Map();

  for (const r of rows) {
    const sortiment = r.sortiment || "—";
    const routeKey = `${r.from || "—"} → ${r.to || "—"}`;
    const date = r.dateISO || "—";
    const amount = Number(r.amount) || 0;

    if (!byAssortment.has(sortiment)) {
      byAssortment.set(sortiment, { sortiment, total: 0, count: 0, routes: new Map() });
    }
    const a = byAssortment.get(sortiment);
    a.total += amount;
    a.count += 1;

    if (!a.routes.has(routeKey)) {
      a.routes.set(routeKey, { route: routeKey, total: 0, count: 0, days: new Map() });
    }
    const rt = a.routes.get(routeKey);
    rt.total += amount;
    rt.count += 1;

    if (!rt.days.has(date)) rt.days.set(date, { date, total: 0, count: 0 });
    const d = rt.days.get(date);
    d.total += amount;
    d.count += 1;
  }

  return Array.from(byAssortment.values())
    .sort((x, y) => y.total - x.total)
    .map((a) => ({
      ...a,
      routes: Array.from(a.routes.values())
        .sort((x, y) => y.total - x.total)
        .map((rt) => ({
          ...rt,
          days: Array.from(rt.days.values()).sort((x, y) => x.date.localeCompare(y.date)),
        })),
    }));
}

function htmlEscape(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ---------- component ---------- */
export default function ReportModal({
  client,
  site,
  clients = [],
  initialStartDate = "",
  initialEndDate = "",
  onSent,
  onClose,
}) {
  const [allWaybills, setAllWaybills] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState("");

  // Pre-filled from the main-view Algus/Lõpp filter; still editable here.
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const [generated, setGenerated] = useState(false);
  const [recipient, setRecipient] = useState(client?.email || "");
  const [ccRecipient, setCcRecipient] = useState(client?.email_cc || "");
  const [subject, setSubject] = useState("");
  const [emailText, setEmailText] = useState("");

  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [templateKey, setTemplateKey] = useState("tavaklient");
  const [textEdited, setTextEdited] = useState(false);

  const [reportBlob, setReportBlob] = useState(null);
  const [filename, setFilename] = useState("");

  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(""); // "", "ok", "error"
  const [sendMessage, setSendMessage] = useState("");

  // Esc closes the modal (except mid-send).
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && !sending) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sending, onClose]);

  // Load e-mail templates (falls back to the built-in defaults on failure).
  useEffect(() => {
    apiFetch("/api/email-templates")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (Array.isArray(data) && data.length) setTemplates(data);
      })
      .catch((err) => console.error("Failed to load templates", err));
  }, []);

  // Load waybills for the selected site once.
  useEffect(() => {
    if (!site) return;
    setLoadingData(true);
    setLoadError("");
    apiFetch(`/api/waybills?site=${site.toLowerCase()}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => setAllWaybills(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Failed to load waybills", err);
        setLoadError("Veoselehtede laadimine ebaõnnestus.");
      })
      .finally(() => setLoadingData(false));
  }, [site]);

  const clientsById = useMemo(() => {
    const m = new Map();
    (clients || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [clients]);

  function companyName(id) {
    const c = clientsById.get(id);
    return c ? c.name : "";
  }

  // A client's group (parent), resolved live from clients.grupp.
  function groupOf(id) {
    const c = clientsById.get(id);
    return c ? c.grupp : id;
  }

  // Rows for the WHOLE group of `client` (a parent) within the date range:
  // any waybill whose sender's or receiver's group resolves to this parent.
  // Detail sheets still show the real sender/receiver companies via companyName.
  const rows = useMemo(() => {
    return allWaybills
      .filter(
        (w) => groupOf(w.sender_id) === client.id || groupOf(w.receiver_id) === client.id
      )
      .map((w) => ({ ...w, dateISO: toISODate(w.created_timestamp) }))
      .filter((w) => {
        if (startDate && w.dateISO < startDate) return false;
        if (endDate && w.dateISO > endDate) return false;
        return true;
      })
      .sort((a, b) => new Date(a.created_timestamp) - new Date(b.created_timestamp));
  }, [allWaybills, client.id, startDate, endDate, clientsById]);

  // Station for this site. Inbound = station is the Sihtkoht (destination);
  // outbound = station is the Lähtekoht (origin). Rows matching neither are dropped.
  const station = site ? `Bioforce ${site} OÜ` : "";
  const inboundRows = useMemo(() => rows.filter((r) => r.to === station), [rows, station]);
  const outboundRows = useMemo(() => rows.filter((r) => r.from === station), [rows, station]);

  const inboundSummary = useMemo(() => buildSummary(inboundRows), [inboundRows]);
  const outboundSummary = useMemo(() => buildSummary(outboundRows), [outboundRows]);

  function applyQuickRange(range) {
    setStartDate(range.start);
    setEndDate(range.end);
    setGenerated(false);
    setSendStatus("");
    setSendMessage("");
  }

  async function buildWorkbookBlob() {
    const wb = new ExcelJS.Workbook();
    const GREEN = "FF92D050"; // inbound
    const BLUE = "FF0070C0"; // outbound

    // Size every column to its widest cell (header included), within limits.
    const autoFitColumns = (ws, { min = 10, max = 55 } = {}) => {
      ws.columns.forEach((col) => {
        let width = 0;
        col.eachCell({ includeEmpty: false }, (cell) => {
          const v = cell.value;
          const len = v === null || v === undefined ? 0 : String(v).length;
          if (len > width) width = len;
        });
        col.width = Math.min(Math.max(width + 2, min), max);
      });
    };

    const centerAll = (ws) => {
      ws.eachRow((row) => {
        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center", vertical: "middle" };
        });
      });
    };

    // Row-level waybills sheet
    const addDetailSheet = (name, dirRows, color) => {
      const ws = wb.addWorksheet(name);
      ws.properties.tabColor = { argb: color };
      ws.columns = [
        { header: "Veoselehe number", key: "id", width: 18 },
        { header: "Sortiment", key: "sortiment", width: 22 },
        { header: "EWC", key: "ewc", width: 12 },
        { header: "Kogus", key: "amount", width: 10 },
        { header: "Aeg", key: "created", width: 18 },
        { header: "Lähtekoht", key: "from", width: 26 },
        { header: "Saatja ettevõte", key: "sender_comp", width: 24 },
        { header: "Saatja reg. kood", key: "sender_regcode", width: 16 },
        { header: "Sihtkoht", key: "to", width: 26 },
        { header: "Saaja ettevõte", key: "receiver_comp", width: 24 },
        { header: "Saaja reg. kood", key: "receiver_regcode", width: 16 },
        { header: "Veofirma", key: "transport_name", width: 24 },
        { header: "Veofirma reg. kood", key: "transport_regcode", width: 16 },
        { header: "Autojuht", key: "driver_name", width: 20 },
        { header: "Sõiduk", key: "truck_nr", width: 14 },
        { header: "Kilomeetrid", key: "mileage", width: 12 },
        { header: "Kinnitamise aeg", key: "confirmed", width: 18 },
      ];
      dirRows.forEach((r) =>
        ws.addRow({
          id: r.id,
          sortiment: r.sortiment,
          ewc: r.ewc,
          amount: Number(r.amount) || 0,
          created: fmtDateTimeEt(r.created_timestamp),
          from: r.from,
          sender_comp: companyName(r.sender_id),
          sender_regcode: r.sender_regcode,
          to: r.to,
          receiver_comp: companyName(r.receiver_id),
          receiver_regcode: r.receiver_regcode,
          transport_name: r.transport_name,
          transport_regcode: r.transport_regcode,
          driver_name: r.driver_name,
          truck_nr: r.truck_nr,
          mileage: r.mileage,
          confirmed: fmtDateTimeEt(r.confirmed_timestamp),
        })
      );
      centerAll(ws);
      ws.getRow(1).font = { bold: true };
      autoFitColumns(ws);
    };

    // Summary by assortment + route
    const addSummarySheet = (name, dirRows, color) => {
      const ws = wb.addWorksheet(name);
      ws.properties.tabColor = { argb: color };
      ws.columns = [
        { header: "Sortiment", key: "sortiment", width: 26 },
        { header: "Lähtekoht → Sihtkoht", key: "route", width: 48 },
        { header: "Kogus", key: "amount", width: 14 },
      ];
      buildSummary(dirRows).forEach((a) => {
        const head = ws.addRow({ sortiment: a.sortiment, route: "KOKKU", amount: a.total });
        head.font = { bold: true };
        a.routes.forEach((rt) =>
          ws.addRow({ sortiment: "", route: `${rt.from} → ${rt.to}`, amount: rt.amount })
        );
      });
      ws.getRow(1).font = { bold: true };
      autoFitColumns(ws);
    };

    // Daily summary, 3 levels: sortiment -> suund (route) -> päevad,
    // with totals on the two upper levels.
    const addDailySheet = (name, dirRows, color) => {
      const ws = wb.addWorksheet(name);
      ws.properties.tabColor = { argb: color };
      ws.columns = [
        { header: "Sortiment / Suund / Kuupäev", key: "label", width: 46 },
        { header: "Kogus", key: "amount", width: 14 },
        { header: "Veoste arv", key: "count", width: 12 },
      ];

      buildDailyHierarchy(dirRows).forEach((a) => {
        // Level 1 — assortment total
        const aRow = ws.addRow({ label: a.sortiment, amount: a.total, count: a.count });
        aRow.font = { bold: true };
        aRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE6ECF8" },
          };
        });
        aRow.getCell("label").alignment = { indent: 0 };

        a.routes.forEach((rt) => {
          // Level 2 — route total
          const rRow = ws.addRow({ label: rt.route, amount: rt.total, count: rt.count });
          rRow.font = { bold: true };
          rRow.getCell("label").alignment = { indent: 2 };

          // Level 3 — per day
          rt.days.forEach((d) => {
            const dRow = ws.addRow({
              label: fmtDateEt(d.date),
              amount: d.total,
              count: d.count,
            });
            dRow.getCell("label").alignment = { indent: 4 };
          });
        });
      });

      ws.getRow(1).font = { bold: true };
      autoFitColumns(ws);
    };

    // Inbound (station = Sihtkoht) — green
    addDetailSheet("Veoselehed_sisse", inboundRows, GREEN);
    addSummarySheet("Koond_jaama", inboundRows, GREEN);
    addDailySheet("Päevade_lõikes_sisse", inboundRows, GREEN);

    // Outbound (station = Lähtekoht) — blue
    addDetailSheet("Veoselehed_välja", outboundRows, BLUE);
    addSummarySheet("Koond_jaamast", outboundRows, BLUE);
    addDailySheet("Päevade_lõikes_välja", outboundRows, BLUE);

    const buffer = await wb.xlsx.writeBuffer();
    return new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  }

  async function handleGenerate() {
    setSendStatus("");
    setSendMessage("");
    const blob = await buildWorkbookBlob();
    // No dates -> no date part in the filename at all.
    const datePart =
      startDate && endDate
        ? `_${fmtDateEt(startDate)}_${fmtDateEt(endDate)}`
        : startDate
        ? `_alates_${fmtDateEt(startDate)}`
        : endDate
        ? `_kuni_${fmtDateEt(endDate)}`
        : "";
    const name = `Veoselehed_${sanitizeFilePart(site || "")}_${sanitizeFilePart(
      client.name
    )}${datePart}.xlsx`;
    setReportBlob(blob);
    setFilename(name);
    setSubject(
      `Bioforce ${site || ""} raport – ${client.name} (${periodLabel(startDate, endDate)})`
    );
    setEmailText(textForTemplate(templateKey));
    setTextEdited(false);
    setGenerated(true);
  }

  // Build the e-mail body from a template + this report's data.
  function textForTemplate(key) {
    const template =
      templates.find((t) => t.key === key) ||
      DEFAULT_TEMPLATES.find((t) => t.key === key) ||
      DEFAULT_TEMPLATES[0];

    const grandTotal =
      inboundSummary.reduce((s, a) => s + a.total, 0) +
      outboundSummary.reduce((s, a) => s + a.total, 0);

    return buildEmailText(template, {
      klient: client.name,
      objekt: site || "",
      periood: periodLabel(startDate, endDate),
      kokku: fmtAmount(grandTotal),
      inboundSummary,
      outboundSummary,
    });
  }

  // Switching template rebuilds the text; ask first if it was hand-edited.
  function switchTemplate(key) {
    if (key === templateKey) return;
    if (
      textEdited &&
      !window.confirm("Muudetud e-kirja tekst asendatakse malli tekstiga. Jätkata?")
    ) {
      return;
    }
    setTemplateKey(key);
    setEmailText(textForTemplate(key));
    setTextEdited(false);
  }

  function handleDownload() {
    if (reportBlob) downloadBlob(reportBlob, filename);
  }

  async function handleSend() {
    setSending(true);
    setSendStatus("");
    setSendMessage("");
    try {
      const blob = reportBlob || (await buildWorkbookBlob());
      const base64 = await blobToBase64(blob);
      const toList = recipient
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean);

      const ccList = ccRecipient
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean);

      if (toList.length === 0) {
        throw new Error("Lisa vähemalt üks e-posti aadress.");
      }

      const html = htmlEscape(emailText).replace(/\n/g, "<br>");

      const res = await apiFetch(`/api/reports/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: toList,
          cc: ccList,
          subject,
          html,
          filename,
          file_base64: base64,
          // Lets the backend stamp report_status.last_sent_at on success.
          client_id: client.id,
          site: site || null,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Saatmine ebaõnnestus (HTTP ${res.status})`);

      if (onSent) onSent(); // refresh the main view's last-sent date
      setSendStatus("ok");
      setSendMessage(
        `Raport saadetud: ${toList.join(", ")}` +
          (ccList.length ? ` (koopia: ${ccList.join(", ")})` : "")
      );
    } catch (err) {
      console.error("Send failed", err);
      setSendStatus("error");
      setSendMessage(err.message || "Saatmine ebaõnnestus.");
    } finally {
      setSending(false);
    }
  }

  const hasRows = rows.length > 0;

  return (
    <div style={overlay}>
      <div style={modal}>
        <h2 style={{ marginTop: 0 }}>Raporti saatmine</h2>
        <p style={{ color: "#555", marginTop: 4 }}>
          {client.name}
          {site ? ` · ${site}` : ""}
        </p>

        {!site && (
          <div style={warnBox}>
            Vali esmalt ülevalt objekt (Aravete / Ebavere / Laatre / Viiratsi).
          </div>
        )}

        {/* Quick ranges */}
        <div style={quickRow}>
          <button style={chip} onClick={() => applyQuickRange(currentMonthRange())}>
            Jooksev kuu
          </button>
          <button style={chip} onClick={() => applyQuickRange(previousMonthRange())}>
            Eelmine kuu
          </button>
          <button style={chip} onClick={() => applyQuickRange(firstHalfRange())}>
            Esimene pool kuud
          </button>
          <button style={chip} onClick={() => applyQuickRange(lastHalfRange())}>
            Viimane pool kuud
          </button>
        </div>

        {/* Date selection */}
        <div style={grid}>
          <div>
            <label style={label}>Algus</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setGenerated(false);
              }}
              style={input}
            />
          </div>
          <div>
            <label style={label}>Lõpp</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setGenerated(false);
              }}
              style={input}
            />
          </div>
        </div>

        <div style={metaRow}>
          {loadingData
            ? "Laen veoselehti…"
            : loadError
            ? loadError
            : `${inboundRows.length} sisse · ${outboundRows.length} välja (${rows.length} kokku)`}
        </div>

        {generated && (
          <>
            {/* Preview */}
            <div style={previewBox}>
              <div style={previewHeaderRow}>
                <strong>Eelvaade — Kokkuvõte</strong>
                <button style={downloadBtn} onClick={handleDownload}>
                  ⬇ Lae alla Excel
                </button>
              </div>

              <div style={previewDirTitle}>▸ Sisse ({inboundRows.length})</div>
              {inboundSummary.length === 0 ? (
                <div style={{ color: "#777", fontSize: 13 }}>—</div>
              ) : (
                inboundSummary.map((a) => (
                  <div key={"in-" + a.sortiment} style={previewLine}>
                    <span>{a.sortiment}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtAmount(a.total)}</span>
                  </div>
                ))
              )}

              <div style={{ ...previewDirTitle, marginTop: 12 }}>
                ▸ Välja ({outboundRows.length})
              </div>
              {outboundSummary.length === 0 ? (
                <div style={{ color: "#777", fontSize: 13 }}>—</div>
              ) : (
                outboundSummary.map((a) => (
                  <div key={"out-" + a.sortiment} style={previewLine}>
                    <span>{a.sortiment}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtAmount(a.total)}</span>
                  </div>
                ))
              )}

              <div style={previewFootNote}>
                Excel: 6 lehte — Veoselehed / Koond / Päevade lõikes, eraldi sisse (roheline) ja
                välja (sinine).
              </div>
            </div>

            {/* Recipient */}
            <label style={label}>Saaja e-post (eralda mitu komaga)</label>
            <input
              type="text"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="klient@example.com, teine@example.com"
              style={input}
            />

            {/* CC */}
            <label style={{ ...label, marginTop: 12 }}>Koopia / CC (eralda mitu komaga)</label>
            <input
              type="text"
              value={ccRecipient}
              onChange={(e) => setCcRecipient(e.target.value)}
              placeholder="koopia@example.com, teine@example.com"
              style={input}
            />

            {/* Subject */}
            <label style={{ ...label, marginTop: 12 }}>Pealkiri</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={input}
            />

            {/* Template selector */}
            <label style={{ ...label, marginTop: 12 }}>Mall</label>
            <div style={templateRow}>
              {templates.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  style={t.key === templateKey ? templateChipActive : templateChip}
                  onClick={() => switchTemplate(t.key)}
                >
                  {t.name}
                </button>
              ))}
            </div>

            {/* Email text */}
            <label style={{ ...label, marginTop: 12 }}>E-kirja tekst</label>
            <textarea
              rows={10}
              value={emailText}
              onChange={(e) => {
                setEmailText(e.target.value);
                setTextEdited(true);
              }}
              style={textarea}
            />

            {sendStatus === "ok" && <div style={okBox}>{sendMessage}</div>}
            {sendStatus === "error" && <div style={errBox}>{sendMessage}</div>}

            <div style={actions}>
              <button style={btnSecondary} onClick={onClose} disabled={sending}>
                Sulge
              </button>
              <button style={sending ? primaryDisabled : primary} onClick={handleSend} disabled={sending}>
                {sending ? "Saadan…" : "Saada raport"}
              </button>
            </div>
          </>
        )}

        {!generated && (
          <div style={actions}>
            <button style={btnSecondary} onClick={onClose}>
              Sulge
            </button>
            <button
              style={hasRows ? primary : primaryDisabled}
              onClick={handleGenerate}
              disabled={!hasRows}
            >
              Genereeri raport
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- styles ---------- */
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  fontFamily: "'DM Sans', sans-serif",
};

const modal = {
  background: "white",
  borderRadius: "14px",
  padding: "24px",
  width: "560px",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
};

const quickRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  margin: "8px 0 16px",
};

const chip = {
  background: "#F3F6FD",
  border: "1px solid #E6ECF8",
  borderRadius: "999px",
  padding: "6px 14px",
  cursor: "pointer",
  fontWeight: 700,
  color: "#0D1F4D",
  fontSize: "13px",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "16px",
  marginBottom: "10px",
};

const templateRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const templateChip = {
  background: "#F3F6FD",
  border: "1px solid #E6ECF8",
  borderRadius: "999px",
  padding: "6px 14px",
  cursor: "pointer",
  fontWeight: 700,
  color: "#0D1F4D",
  fontSize: "13px",
};

const templateChipActive = {
  ...templateChip,
  background: "#0D1F4D",
  border: "1px solid #0D1F4D",
  color: "white",
};

const label = {
  display: "block",
  fontWeight: 600,
  color: "#0D1F4D",
  marginBottom: "6px",
  fontSize: "14px",
};

const input = {
  width: "100%",
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #8CA5E6",
  fontSize: "14px",
  boxSizing: "border-box",
  fontFamily: "'DM Sans', sans-serif",
};

const metaRow = {
  fontSize: "13px",
  color: "#555",
  margin: "6px 0 14px",
};

const primary = {
  background: "#0D1F4D",
  color: "white",
  border: "none",
  padding: "11px 16px",
  borderRadius: "8px",
  fontWeight: 700,
  cursor: "pointer",
  width: "100%",
};

const primaryDisabled = {
  ...primary,
  background: "#9AA8C7",
  cursor: "not-allowed",
};

const previewBox = {
  background: "#F3F6FD",
  padding: "14px",
  borderRadius: "10px",
  margin: "16px 0",
};

const previewHeaderRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const previewDirTitle = {
  fontWeight: 700,
  color: "#0D1F4D",
  fontSize: "13px",
  margin: "8px 0 4px",
};

const previewLine = {
  display: "flex",
  justifyContent: "space-between",
  padding: "4px 0",
  fontSize: "14px",
  color: "#0D1F4D",
  borderBottom: "1px solid #E6ECF8",
};

const previewFootNote = {
  marginTop: "10px",
  fontSize: "12px",
  color: "#666",
};

const downloadBtn = {
  background: "#8CA5E6",
  color: "white",
  border: "none",
  padding: "7px 12px",
  borderRadius: "8px",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: "13px",
};

const textarea = {
  width: "100%",
  padding: "12px",
  borderRadius: "8px",
  border: "1px solid #8CA5E6",
  fontSize: "14px",
  boxSizing: "border-box",
  fontFamily: "'DM Sans', sans-serif",
  whiteSpace: "pre-wrap",
};

const actions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  marginTop: "20px",
};

const btnSecondary = {
  background: "#8CA5E6",
  color: "white",
  border: "none",
  padding: "10px 16px",
  borderRadius: "8px",
  fontWeight: 600,
  cursor: "pointer",
};

const warnBox = {
  background: "#FFF4E5",
  border: "1px solid #FFD8A8",
  color: "#8a5a00",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "13px",
  margin: "8px 0",
};

const okBox = {
  background: "#E7F6EC",
  border: "1px solid #B6E2C6",
  color: "#1E8449",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "13px",
  marginTop: "14px",
  fontWeight: 600,
};

const errBox = {
  background: "#FDECEA",
  border: "1px solid #F5C6C0",
  color: "#C0392B",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "13px",
  marginTop: "14px",
  fontWeight: 600,
};
